import { useState, useCallback } from 'react';
import { getSavedSearches } from 'data/saved-searches';
import { getUserLastSearch } from 'pages/api/last-search/lastSearchApiClient';
import { ThemeNames } from 'types/themes';
import { useUserContext } from 'contexts';

import type SavedSearch from 'data/saved-searches';
import type { GetUserLastSearchResponse } from 'pages/api/last-search/types';

interface SavedSearchesCache {
  savedSearches: SavedSearch[];
  lastSearch?: GetUserLastSearchResponse;
  timestamp: number;
}

interface UseSavedSearchesReturn {
  savedSearches: SavedSearch[];
  lastSearch?: GetUserLastSearchResponse;
  isLoading: boolean;
  fetchSavedSearches: () => Promise<void>;
  prefetchSavedSearches: () => void;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useSavedSearches(): UseSavedSearchesReturn {
  const { user } = useUserContext();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [lastSearch, setLastSearch] = useState<GetUserLastSearchResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [cache, setCache] = useState<SavedSearchesCache | null>(null);

  const fetchSavedSearches = useCallback(async () => {
    if (isLoading) return; // Prevent duplicate requests
    
    // Check cache first
    const now = Date.now();
    if (cache && (now - cache.timestamp) < CACHE_DURATION) {
      setSavedSearches(cache.savedSearches);
      if (cache.lastSearch) {
        setLastSearch(cache.lastSearch);
      }
      return;
    }

    setIsLoading(true);
    
    try {
      // Run API calls in parallel
      const [savedSearchesResponse, lastSearchResponse] = await Promise.all([
        getSavedSearches(3, undefined, ThemeNames.ZOOCASA),
        user ? getUserLastSearch({ userId: user.id, sourceTheme: ThemeNames.ZOOCASA }) : Promise.resolve(null),
      ]);

      // Update state
      setSavedSearches(savedSearchesResponse.savedSearches);
      
      let resolvedLastSearch: GetUserLastSearchResponse | undefined;
      if (lastSearchResponse && 'lastSearch' in lastSearchResponse) {
        resolvedLastSearch = lastSearchResponse;
        setLastSearch(lastSearchResponse);
      }

      // Update cache
      setCache({
        savedSearches: savedSearchesResponse.savedSearches,
        lastSearch: resolvedLastSearch,
        timestamp: now,
      });
    } catch (error) {
      console.error('Error loading saved searches:', error);
      setSavedSearches([]);
      setLastSearch(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [user, isLoading, cache]);

  const prefetchSavedSearches = useCallback(() => {
    // Only prefetch if we don't have recent cached data
    const now = Date.now();
    if (!cache || (now - cache.timestamp) >= CACHE_DURATION) {
      fetchSavedSearches();
    }
  }, [cache, fetchSavedSearches]);

  return {
    savedSearches,
    lastSearch,
    isLoading,
    fetchSavedSearches,
    prefetchSavedSearches,
  };
} 